/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC, useState, useEffect, useRef, DragEvent } from 'react';
import CrossIcon from '../../../../assets/images/svg/CrossIcon';
import { CustomClassContainer, CustomClassContainerProps } from '../../utility';
import { useAppSelector } from '../../../infra/redux';
import { useNotificationCenter } from '../../toast';
import { classNames } from '../../../utils';
import FilePlaceholderIcon from '../../../../assets/images/svg/FilePlaceholderIcon';

enum UploadTypes {
  FILES = 'FILES',
  IMAGES = 'IMAGES',
}

const UPLOAD_TYPES_MAP: Record<UploadTypes, string[]> = {
  [UploadTypes.FILES]: [
    'application/pdf',
    'application/txt',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
  ],
  [UploadTypes.IMAGES]: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/x-icon',
  ],
};

export interface ValidatedFile extends File {
  invalid?: boolean;
}

interface SingleFileUploadProps extends CustomClassContainerProps {
  variant?: keyof typeof UploadTypes;
  label: string;
  value?: string;
  name?: string;
  onUpload: (file: ValidatedFile) => Promise<void>;
}

const SingleFileUpload: FC<SingleFileUploadProps> = ({
  variant = 'IMAGES',
  label,
  name,
  value,
  onUpload,
  classes,
  children,
}) => {
  const uploadPercentage = useAppSelector(
    (state) => state.files.percentageUploaded,
  );
  const uploadStatus = useAppSelector(
    (state) => state.files.fileUploadStatus.status,
  );
  const [isImageLoaded, setisImageLoaded] = useState<boolean>(true);
  const { addNotification } = useNotificationCenter();
  const [isUploaded, setIsUploaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<ValidatedFile | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressRef = useRef<HTMLSpanElement>(null);
  const progressPercentRef = useRef<HTMLDivElement>(null);

  const handleOnDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleOnDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleOnDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleOnDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 1) {
      addNotification(
        {
          title: 'Error',
          description:
            'Input does not support multiple filer. Drop a single file!',
        },
        {
          variant: 'ERROR',
        },
      );
      return;
    }
    handleSelectedFile(e.dataTransfer.files);
  };

  const handleSelectedFile = (files: FileList) => {
    const file: ValidatedFile = files[0];
    if (validateFile(file)) {
      setSelectedFile(file);
    } else {
      addNotification(
        {
          title: 'Error',
          description: 'File type was invalid. Please try a different file.',
        },
        {
          variant: 'ERROR',
        },
      );
    }
  };

  const validateFile = (file: File | null): boolean => {
    const validFileTypes = UPLOAD_TYPES_MAP[variant];
    if (!file || validFileTypes.indexOf(file.type) === -1) {
      return false;
    }

    return true;
  };

  const fileType = (file: File | null): string => {
    if (file) {
      return file.type;
    }

    return 'Undefined';
  };

  const calculatedFileSize = (size: number): string => {
    if (size === 0) return '0 bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${parseFloat((size / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const onFileInputClicked = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isImageFile = () => {
    if (!selectedFile) return false;

    return UPLOAD_TYPES_MAP.IMAGES.includes(selectedFile.type);
  };

  const onFileInputChage = () => {
    if (fileInputRef.current && fileInputRef.current.files?.length) {
      handleSelectedFile(fileInputRef.current.files);
    }
  };

  const onRemoveFile = () => {
    setSelectedFile(null);
    setIsUploaded(false);
  };

  useEffect(() => {
    if (progressRef.current && progressPercentRef.current) {
      progressRef.current.style.width = `${
        uploadPercentage ? uploadPercentage : 0
      }%`;
      progressPercentRef.current.innerHTML = `${
        uploadPercentage ? uploadPercentage : 0
      }%`;
    }
    if (uploadPercentage === 100) {
      setIsUploaded(true);
    }
  }, [uploadPercentage]);

  useEffect(() => {
    if (!selectedFile || selectedFile.invalid) return;

    void onUpload(selectedFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);
  return (
    <CustomClassContainer classes={classes}>
      <div className='flex flex-1 flex-grow-0 flex-col'>
        <div className='mb-2 flex flex-row items-center justify-between'>
          <label
            className='text-base font-medium tracking-tight text-gray-dark'
            htmlFor={name ? name : 'fileUpload'}
          >
            {label}
          </label>
          {children ? <div>{children}</div> : null}
        </div>
        <div className='flex w-full flex-row items-center space-x-4 rounded-lg border border-gray-light px-3 py-2 shadow-sm'>
          {value && !selectedFile ? (
            <div className='h-48 w-80 overflow-hidden rounded-lg shadow-sm'>
              {isImageLoaded ? (
                <img
                  className='h-full w-full object-cover object-center'
                  src={value}
                  alt='current uploaded'
                  onLoad={() => setisImageLoaded(true)}
                  onError={() => setisImageLoaded(false)}
                />
              ) : (
                <div className='flex h-full w-full flex-col items-center justify-center'>
                  <FilePlaceholderIcon
                    width='100%'
                    height='60%'
                    className='stroke-current text-gray-light'
                  />
                  <span className='mt-1 break-words px-10 text-center text-xxs font-bold uppercase text-gray-light'>
                    {value.split('/')[value.split('/').length - 1]}
                  </span>
                </div>
              )}
            </div>
          ) : null}
          <div className='w-full flex-1'>
            {selectedFile ? (
              <div className='relative px-4 py-4 text-base font-medium'>
                <button
                  onClick={() => onRemoveFile()}
                  className='500ms absolute top-0 right-0 py-4 px-4 text-gray-light transition ease-in-out hover:text-gray'
                >
                  <CrossIcon />
                </button>

                <div className='flex flex-row items-center justify-start'>
                  <div className='h-48 w-80 overflow-hidden rounded-lg shadow-sm'>
                    {value && !selectedFile && (
                      <img
                        className='h-full w-full object-cover object-center'
                        src={value}
                        alt='current uploaded'
                      />
                    )}
                    {!isImageFile() ? (
                      <div className='flex h-full w-full flex-col items-center justify-center'>
                        <FilePlaceholderIcon
                          width='100%'
                          height='60%'
                          className='stroke-current text-gray-light'
                        />
                        <span className='mt-1 text-lg font-bold uppercase text-gray-light'>
                          File
                        </span>
                      </div>
                    ) : (
                      <img
                        className='h-full w-full object-cover object-center'
                        src={URL.createObjectURL(selectedFile)}
                        alt='upload'
                      />
                    )}
                  </div>

                  <div className='ml-10 mr-6 flex-1'>
                    <div className='mb-2 text-base font-medium'>
                      {selectedFile.name}
                    </div>
                    <div className='mb-4 flex flex-row items-center space-x-2'>
                      <div
                        className={classNames(
                          'rounded-full border px-3 py-1 text-xs font-semibold',
                          uploadStatus !== 'failure'
                            ? 'border-green bg-green-light text-green'
                            : 'border-red bg-red-light text-red',
                        )}
                      >
                        {fileType(selectedFile)}
                      </div>
                      <div
                        className={classNames(
                          'rounded-full border px-3  py-1 text-xs font-semibold',
                          uploadStatus !== 'failure'
                            ? 'border-green bg-green-light text-green'
                            : 'border-red bg-red-light text-red',
                        )}
                      >
                        {calculatedFileSize(selectedFile.size)}
                      </div>
                    </div>

                    <div className='relative mb-1 h-1 w-full rounded-md bg-gray-light'>
                      <span
                        className={classNames(
                          'absolute left-0 top-0 h-1 rounded-md',
                          uploadStatus !== 'failure' ? 'bg-green' : 'bg-red',
                        )}
                        ref={progressRef}
                      ></span>
                    </div>
                    {isUploaded ? (
                      uploadStatus === 'loading' ? (
                        <div className='h-4 text-right text-xs font-medium text-gray-light'>
                          File Processing...
                        </div>
                      ) : uploadStatus === 'failure' ? (
                        <div className='h-4 text-right text-xs font-medium text-gray-light'>
                          Upload Failure
                        </div>
                      ) : (
                        <div className='h-4 text-right text-xs font-medium text-gray-light'>
                          Upload Complete
                        </div>
                      )
                    ) : (
                      <div
                        ref={progressPercentRef}
                        className='h-4 text-right text-xs font-medium text-gray-light'
                      ></div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className='h-52 cursor-pointer appearance-none text-base font-medium focus:outline-none focus:ring-2 focus:ring-green'
                onDragOver={handleOnDragOver}
                onDragEnter={handleOnDragEnter}
                onDragLeave={handleOnDragLeave}
                onDrop={handleOnDrop}
                onClick={onFileInputClicked}
              >
                <input
                  type='file'
                  id={name}
                  className='hidden'
                  ref={fileInputRef}
                  onChange={onFileInputChage}
                />
                <div
                  className={classNames(
                    value
                      ? 'flex flex-col space-y-1 text-center'
                      : 'flex flex-row space-x-3 ',
                    'h-full w-full items-center justify-center text-sm font-medium text-gray-light',
                  )}
                >
                  <p className='text-center'>Drag and Drop File</p>
                  <p className='text-xs'>&bull;</p>
                  <p className='text-center'>Click To Choose File</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CustomClassContainer>
  );
};

export default SingleFileUpload;
